'use client';
import { RecurrenceType } from '@/components/predic/detail/market/crypto-utils';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { PolymarketMarket } from './types';

dayjs.extend(utc);
dayjs.extend(timezone);

export const recurrenceToMs: Record<RecurrenceType, number> = {
  '5m': 5 * 60 * 1000,
  '15m': 15 * 60 * 1000,
  '1h': 60 * 60 * 1000,
  '4h': 4 * 60 * 60 * 1000,
  daily: 24 * 60 * 60 * 1000,
  weekly: 7 * 24 * 60 * 60 * 1000,
  monthly: 30 * 24 * 60 * 60 * 1000,
  yearly: 365 * 24 * 60 * 60 * 1000,
};

export const recurrenceIntervalToMs: Record<RecurrenceType, number> = {
  '5m': 5 * 60 * 1000,
  '15m': 15 * 60 * 1000,
  '1h': 60 * 60 * 1000,
  '4h': 4 * 60 * 60 * 1000,
  daily: 24 * 60 * 60 * 1000,
  weekly: 1 * 24 * 60 * 60 * 1000,
  monthly: 30 * 24 * 60 * 60 * 1000,
  yearly: 365 * 24 * 60 * 60 * 1000,
};

export const TZ = {
  ET: 'America/New_York',
  PT: 'America/Los_Angeles',
  UTC: 'UTC',
} as const;

export const monthMap: Record<string, number> = {
  january: 1,
  february: 2,
  march: 3,
  april: 4,
  may: 5,
  june: 6,
  july: 7,
  august: 8,
  september: 9,
  october: 10,
  november: 11,
  december: 12,

  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  sept: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};

// btc-updown-5m-1777453200
// doge-updown-15m-1777453200
// sol-updown-4h-1777449600
// bitcoin-up-or-down-april-29-2026-5am-et
// bitcoin-up-or-down-on-april-29-2026
// what-price-will-solana-hit-on-april-29
// bitcoin-price-on-april-29
// what-price-will-ethereum-hit-in-april-2026

export function matchEtTimeEnd(slug: string) {
  const match = slug.match(/([a-z]+)-(\d+)-(\d+)-(\d+)(am|pm)-et/i);
  if (match) {
    const [, monthStr, day, year, hour, ap] = match;
    return [monthStr, day, year, hour, ap];
  }
  return null;
}

export function parseSlug2StartTimestamp(slug: string) {
  if (/\d{10}$/.test(slug)) {
    return slug.split('-').at(-1) ? parseInt(slug.split('-').at(-1)!, 10) * 1000 : Date.now();
  }
  if (slug.endsWith('-et')) {
    const match = matchEtTimeEnd(slug);
    if (match) {
      const [monthStr, day, year, hour, ap] = match;

      let h = Number(hour);
      if (ap.toLowerCase() === 'pm' && h !== 12) h += 12;
      if (ap.toLowerCase() === 'am' && h === 12) h = 0;

      const dateStr = `${year}-${monthMap[monthStr.toLowerCase()]}-${day} ${h}:00:00`;

      return dayjs.tz(dateStr, TZ.ET).valueOf();
    }
  }

  const match = slug.match(
    /(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec|january|february|march|april|june|july|august|september|october|november|december)-(?:\d{1,2}(?:-\d{4})?|\d{4})$/i,
  );
  if (match) {
    const [matched] = match;
    const parts = matched.split('-');
    const monthStr = parts[0];
    let month = monthMap[monthStr.toLowerCase()];
    let day = 1;
    let year = dayjs().year();
    if (parts.length === 3) {
      // apr-29-2026
      day = Number(parts[1]);
      year = Number(parts[2]);
    } else if (parts.length === 2) {
      if (parts[1].length === 4) {
        // april-2026
        year = Number(parts[1]);
        return dayjs(`${year}-${month}-${day}`).add(12, 'h').valueOf();
      } else {
        // april-29
        day = Number(parts[1]);
      }
    }
    console.log('parsed date from slug:', dayjs(`${year}-${month}-${day}`).valueOf());
    return dayjs(`${year}-${month}-${day}`).valueOf();
  }
  return Date.now();
}

export function parseSlug2EndTimestamp(slug: string, recurrence: RecurrenceType) {
  if (/\d{10}$/.test(slug)) {
    const start = slug.split('-').at(-1)
      ? parseInt(slug.split('-').at(-1)!, 10) * 1000
      : Date.now();
    return start + recurrenceToMs[recurrence];
  }

  if (slug.endsWith('-et')) {
    const match = matchEtTimeEnd(slug);
    if (match) {
      const [monthStr, day, year, hour, ap] = match;

      let h = Number(hour);
      if (ap.toLowerCase() === 'pm' && h !== 12) h += 12;
      if (ap.toLowerCase() === 'am' && h === 12) h = 0;

      const dateStr = `${year}-${monthMap[monthStr.toLowerCase()]}-${day} ${h}:00:00`;

      return dayjs.tz(dateStr, TZ.ET).valueOf();
    }
  }

  const match = slug.match(
    /(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec|january|february|march|april|june|july|august|september|october|november|december)-(?:\d{1,2}(?:-\d{4})?|\d{4})$/i,
  );
  if (match) {
    const [matched] = match;
    const parts = matched.split('-');
    const monthStr = parts[0];
    let month = monthMap[monthStr.toLowerCase()];
    let day = 1;
    let year = dayjs().year();
    if (parts.length === 3) {
      // apr-29-2026
      day = Number(parts[1]);
      year = Number(parts[2]);
    } else if (parts.length === 2) {
      if (parts[1].length === 4) {
        // april-2026
        year = Number(parts[1]);
        return dayjs(`${year}-${month}-${day}`).endOf('month').add(12, 'h').valueOf();
      } else {
        // april-29
        day = Number(parts[1]);
      }
    }
    console.log('parsed date from slug:', dayjs(`${year}-${month}-${day}`).valueOf());
    return dayjs(`${year}-${month}-${day}`).valueOf();
  }
  return Date.now();
}

export function generateSlugByTimestamp(slug: string, timestamp: number) {
  if (/\d{10}$/.test(slug)) {
    return slug.replace(/\d{10}$/, `${timestamp / 1000}`);
  }

  const et = dayjs(timestamp).tz('America/New_York');
  if (slug.endsWith('-et')) {
    const match = matchEtTimeEnd(slug);
    if (match) {
      return slug.replace(
        /([a-z]+)-(\d+)-(\d+)-(\d+)(am|pm)-et$/i,
        `${et.format('MMMM-D-YYYY-ha')}-et`,
      );
    }
  }

  const match = slug.match(
    /(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec|january|february|march|april|june|july|august|september|october|november|december)-(?:\d{1,2}(?:-\d{4})?|\d{4})$/i,
  );
  if (match) {
    const [matched] = match;
    const parts = matched.split('-');

    const segments = slug.split('-');
    if (parts.length === 3) {
      return [...segments.slice(0, segments.length - 3), et.format('MMMM-D-YYYY')].join('-');
    } else if (parts.length === 2) {
      if (parts[1].length === 4) {
        // april-2026
        return [
          ...segments.slice(0, segments.length - 2),
          et.subtract(1, 'month').format('MMMM-YYYY'),
        ].join('-');
      } else {
        // april-29
        return [...segments.slice(0, segments.length - 2), et.format('MMMM-D')].join('-');
      }
    }
  }

  return slug;
}

function string2Number(str: string) {
  return parseFloat(str || '0');
}

export function isRepeatMarkets(markets: PolymarketMarket[]): boolean {
  const titleCounts = new Map<string, number>();

  for (const market of markets) {
    const title = market.group_item_title;
    // 只统计非空的 group_item_title
    if (title && title.trim() !== '') {
      titleCounts.set(title, (titleCounts.get(title) || 0) + 1);
      // 如果发现重复，立即返回 true
      if (titleCounts.get(title)! > 1) {
        return true;
      }
    }
  }

  return false;
}

export function getMarketDistributionType(slug: string) {
  if (/world-cup-group-[a-z]-winner/i.test(slug)) {
    return 'group';
  }

  // 送出助攻次数最多的球员为获胜方。
  if (slug === 'world-cup-most-assists') {
    return 'assists';
  }

  if (slug === 'world-cup-winner') {
    return 'world-cup-winner';
  }

  if (slug === 'world-cup-top-scorer-nation') {
    return 'world-cup-top-scorer-nation';
  }

  // 贡献进球助攻次数最多的球员
  if (slug === 'world-cup-most-goal-contributions') {
    return 'world-cup-most-goal-contributions';
  }

  if (slug === 'world-cup-golden-ball-winner-20260603194031758') {
    return 'golden-ball-winner';
  }

  if (slug === 'world-cup-golden-boot-winner') {
    return 'world-cup-golden-boot-winner';
  }
  return 'chart';
}
